/**
 * Site Configuration Provider
 * Fetches UI config from backend and provides it to the app
 * Applies design tokens as CSS variables
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

const SiteConfigContext = createContext(null);

// Default tokens fallback
const DEFAULT_TOKENS = {
  colors: {
    primary: '#f97316',
    secondary: '#38bdf8',
    background: '#0b0b0b',
    surface: '#141414',
    surface2: '#1f1f1f',
    textPrimary: '#ffffff',
    textSecondary: '#cfcfcf',
    border: '#2a2a2a',
    danger: '#ef4444',
    warning: '#f59e0b',
    success: '#22c55e'
  },
  typography: {
    fontFamilyHeading: 'Inter',
    fontFamilyBody: 'Inter',
    baseFontSize: 16,
    scaleRatio: 1.25,
    lineHeight: 1.5,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700
  },
  spacing: { xs: 4, sm: 8, md: 16, lg: 24, xl: 32, xxl: 48 },
  radius: { sm: 6, md: 12, lg: 18, xl: 24 },
  shadow: {
    sm: '0 1px 2px rgba(0,0,0,0.25)',
    md: '0 6px 16px rgba(0,0,0,0.3)'
  }
};

// Convert tokens to CSS custom properties
const tokensToCSS = (tokens) => {
  const vars = {};
  
  // Colors
  if (tokens.colors) {
    Object.entries(tokens.colors).forEach(([key, value]) => {
      vars[`--color-${key}`] = value;
    });
  }
  
  // Typography
  if (tokens.typography) {
    vars['--font-family-heading'] = tokens.typography.fontFamilyHeading;
    vars['--font-family-body'] = tokens.typography.fontFamilyBody;
    vars['--font-size-base'] = `${tokens.typography.baseFontSize}px`;
    vars['--font-scale'] = tokens.typography.scaleRatio;
    vars['--line-height'] = tokens.typography.lineHeight;
    vars['--font-weight-regular'] = tokens.typography.fontWeightRegular;
    vars['--font-weight-medium'] = tokens.typography.fontWeightMedium;
    vars['--font-weight-bold'] = tokens.typography.fontWeightBold;
  }
  
  // Spacing
  if (tokens.spacing) {
    Object.entries(tokens.spacing).forEach(([key, value]) => {
      vars[`--spacing-${key}`] = `${value}px`;
    });
  }
  
  // Radius
  if (tokens.radius) {
    Object.entries(tokens.radius).forEach(([key, value]) => {
      vars[`--radius-${key}`] = `${value}px`;
    });
  }
  
  // Shadows
  if (tokens.shadow) {
    Object.entries(tokens.shadow).forEach(([key, value]) => {
      vars[`--shadow-${key}`] = value;
    });
  }
  
  return vars;
};

// Apply CSS variables to document
const applyCSSVariables = (cssVars) => {
  const root = document.documentElement;
  Object.entries(cssVars).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
};

export const SiteConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/site-config`);
      const data = response.data;
      
      setConfig(data);
      
      // Apply tokens as CSS variables
      const tokens = data.tokens || DEFAULT_TOKENS;
      const cssVars = tokensToCSS(tokens);
      applyCSSVariables(cssVars);
      
      setError(null);
    } catch (err) {
      console.error('Failed to fetch site config:', err);
      setError(err);
      
      // Apply default tokens on error
      const cssVars = tokensToCSS(DEFAULT_TOKENS);
      applyCSSVariables(cssVars);
      
      // Use default config
      setConfig({
        version: 0,
        tokens: DEFAULT_TOKENS,
        layouts: {},
        presets: {},
        feature_flags: {}
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Get layout by key (e.g., 'home')
  const getLayout = useCallback((layoutKey) => {
    return config?.layouts?.[layoutKey] || null;
  }, [config]);

  // Get preset by key (e.g., 'hero.default')
  const getPreset = useCallback((presetKey) => {
    return config?.presets?.[presetKey] || null;
  }, [config]);

  // Check feature flag
  const isFeatureEnabled = useCallback((flagKey) => {
    return config?.feature_flags?.[flagKey] ?? false;
  }, [config]);

  // Get token value
  const getToken = useCallback((category, key) => {
    return config?.tokens?.[category]?.[key] || DEFAULT_TOKENS[category]?.[key];
  }, [config]);

  // Refresh config (for admin after save)
  const refreshConfig = useCallback(async () => {
    setLoading(true);
    await fetchConfig();
  }, [fetchConfig]);

  const value = {
    config,
    loading,
    error,
    version: config?.version || 0,
    tokens: config?.tokens || DEFAULT_TOKENS,
    layouts: config?.layouts || {},
    presets: config?.presets || {},
    featureFlags: config?.feature_flags || {},
    getLayout,
    getPreset,
    isFeatureEnabled,
    getToken,
    refreshConfig
  };

  return (
    <SiteConfigContext.Provider value={value}>
      {children}
    </SiteConfigContext.Provider>
  );
};

export const useSiteConfig = () => {
  const context = useContext(SiteConfigContext);
  if (!context) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return context;
};

export default SiteConfigContext;
